import { z } from 'zod'
import { LOCATION_CAPABILITIES, LOCATION_TYPES, SUPPORTED_MODES } from '../lib/locationOptions'

const trimmedNullable = z.string().trim().max(255).optional().nullable()

const coordinateSchema = z.coerce.number().finite()

export const locationAddressSchema = z.object({
  street: z.string().trim().min(1),
  city: z.string().trim().min(1),
  postalCode: z.string().trim().min(1),
  country: z.string().trim().min(2).max(100),
  region: trimmedNullable,
})

export const locationOperatingHoursSchema = z
  .record(
    z.string(),
    z.object({
      open: trimmedNullable,
      close: trimmedNullable,
    }),
  )
  .optional()
  .nullable()

const locationPayloadFields = {
  name: z.string().trim().min(1).max(255),
  code: trimmedNullable,
  type: z.enum(LOCATION_TYPES),
  unlocode: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{2}[A-Z0-9]{3}$/)
    .optional()
    .nullable(),
  address: locationAddressSchema,
  supportedModes: z.array(z.enum(SUPPORTED_MODES)).min(1),
  capabilities: z.array(z.enum(LOCATION_CAPABILITIES)).default([]),
  latitude: coordinateSchema.min(-90).max(90),
  longitude: coordinateSchema.min(-180).max(180),
  geofenceRadiusM: z.coerce.number().int().positive().max(200000).optional().nullable(),
  slotBookingEnabled: z.boolean().optional(),
  operatingHours: locationOperatingHoursSchema,
  avgHandlingTimeMin: z.coerce.number().int().positive().max(10080).optional().nullable(),
  contactName: trimmedNullable,
  contactPhone: z.string().trim().max(100).optional().nullable(),
  gateInstructions: z.string().trim().max(4000).optional().nullable(),
  isActive: z.boolean().optional(),
}

export const locationPayloadSchema = z.object(locationPayloadFields)

export const locationCreateSchema = locationPayloadSchema.extend({
  tenantId: z.string().uuid(),
  organizationId: z.string().uuid(),
})

export const locationUpdateSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string().trim().min(1).max(255).optional(),
    code: trimmedNullable,
    type: z.enum(LOCATION_TYPES).optional(),
    unlocode: z
      .string()
      .trim()
      .toUpperCase()
      .regex(/^[A-Z]{2}[A-Z0-9]{3}$/)
      .optional()
      .nullable(),
    address: locationAddressSchema.partial().optional(),
    supportedModes: z.array(z.enum(SUPPORTED_MODES)).min(1).optional(),
    capabilities: z.array(z.enum(LOCATION_CAPABILITIES)).optional(),
    latitude: coordinateSchema.min(-90).max(90).optional(),
    longitude: coordinateSchema.min(-180).max(180).optional(),
    geofenceRadiusM: z.coerce.number().int().positive().max(200000).optional().nullable(),
    slotBookingEnabled: z.boolean().optional(),
    operatingHours: locationOperatingHoursSchema,
    avgHandlingTimeMin: z.coerce.number().int().positive().max(10080).optional().nullable(),
    contactName: trimmedNullable,
    contactPhone: z.string().trim().max(100).optional().nullable(),
    gateInstructions: z.string().trim().max(4000).optional().nullable(),
    isActive: z.boolean().optional(),
  })
  .superRefine((value, ctx) => {
    const latitudeProvided = value.latitude !== undefined
    const longitudeProvided = value.longitude !== undefined
    if (latitudeProvided !== longitudeProvided) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Latitude and longitude must be provided together.',
        path: ['latitude'],
      })
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Latitude and longitude must be provided together.',
        path: ['longitude'],
      })
    }
  })

export const locationDeleteSchema = z.object({
  id: z.string().uuid(),
})

export const locationListQuerySchema = z
  .object({
    page: z.coerce.number().min(1).default(1),
    pageSize: z.coerce.number().min(1).max(100).default(50),
    search: z.string().optional(),
    ids: z.string().optional(),
    type: z.enum(LOCATION_TYPES).optional(),
    supportedMode: z.enum(SUPPORTED_MODES).optional(),
    capability: z.enum(LOCATION_CAPABILITIES).optional(),
    isActive: z.string().optional(),
    slotBookingEnabled: z.string().optional(),
    sortField: z.string().optional(),
    sortDir: z.enum(['asc', 'desc']).optional(),
  })
  .passthrough()

export const nearbyLocationsQuerySchema = z.object({
  lat: coordinateSchema.min(-90).max(90),
  lng: coordinateSchema.min(-180).max(180),
  radiusKm: z.coerce.number().positive().max(500).default(50),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  type: z.enum(LOCATION_TYPES).optional(),
  supportedMode: z.enum(SUPPORTED_MODES).optional(),
  capability: z.enum(LOCATION_CAPABILITIES).optional(),
})

export type LocationCreateInput = z.infer<typeof locationCreateSchema>
export type LocationPayloadInput = z.infer<typeof locationPayloadSchema>
export type LocationUpdateInput = z.infer<typeof locationUpdateSchema>
export type LocationDeleteInput = z.infer<typeof locationDeleteSchema>
export type LocationListQuery = z.infer<typeof locationListQuerySchema>
export type NearbyLocationsQuery = z.infer<typeof nearbyLocationsQuerySchema>
